import { useState } from 'react';
import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HOTELS_QUERY_KEY,
  deleteHotel,
  getHotel,
  getHotelDeletePreview,
  triggerIntake,
  updateHotel,
  type Hotel,
} from '../features/hotels/api.js';
import { IntakeStatusBanner } from '../features/hotels/intake-status.js';
import { HotelDeleteConfirm } from '../features/hotels/delete-confirm.js';
import { getFavoriteHotels, toggleFavoriteHotel } from '../features/dashboard/api.js';
import { StarIcon, RotateCcwIcon, TrashIcon } from '../components/ui/icons.js';
import { isSafeHttpUrl } from '../lib/utils.js';

/**
 * Display host for a hotel website URL. Parses defensively so a malformed
 * value (e.g. a bare `gardenhotel.hu` with no protocol) cannot throw during
 * render and crash the page — there is no error boundary here.
 */
function safeUrlHost(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function HotelDetailShell() {
  const { hotelId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const { data: hotel } = useQuery<Hotel>({
    queryKey: ['hotel', hotelId],
    queryFn: () => getHotel(hotelId),
  });

  // Loaded only when the user opens the confirm dialog — keeps the page
  // initial render light and avoids fetching plans+posts for every hotel
  // page view just in case the user wants to delete.
  // One request, and it throws. This used to fetch the hotel's plans and then
  // one posts list PER plan, swallowing each per-plan failure into `[]` — so a
  // partial failure showed a confidently understated "what will be deleted"
  // count. In a destructive confirmation that is the worst possible failure
  // mode, so the dialog now refuses to confirm rather than guess (see below).
  const cascadeCounts = useQuery({
    queryKey: ['hotel-delete-preview', hotelId],
    enabled: confirmDelete,
    queryFn: () => getHotelDeletePreview(hotelId),
  });

  const toggleActive = useMutation({
    mutationFn: (next: boolean) => updateHotel(hotelId, { isActive: next }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hotel', hotelId] });
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
    },
  });

  const favoritesQ = useQuery<string[]>({
    queryKey: ['dashboard-favorites'],
    queryFn: getFavoriteHotels,
  });
  const isFavorite = (favoritesQ.data ?? []).includes(hotelId);

  const toggleFavorite = useMutation({
    mutationFn: () => toggleFavoriteHotel(hotelId),
    onSuccess: (res) => {
      qc.setQueryData(['dashboard-favorites'], res.favoriteHotelIds);
    },
  });

  const recrawl = useMutation({
    mutationFn: () => triggerIntake(hotelId, hotel?.websiteUrl ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['intake-status', hotelId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteHotel(hotelId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
      void navigate({ to: '/app/hotels' });
    },
  });

  const sinceLabel = hotel?.createdAt
    ? `${new Date(hotel.createdAt).getFullYear()}.${String(new Date(hotel.createdAt).getMonth() + 1).padStart(2, '0')}. óta`
    : '';

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="text-body text-[var(--fg-2)]">
        <Link to="/app/hotels" className="hover:text-[var(--ink-700)] hover:underline">
          Hotelek
        </Link>
        <span className="mx-2 text-[var(--fg-faint)]">›</span>
        <span className="text-[var(--ink-700)]">{hotel?.name ?? '…'}</span>
      </nav>

      {/* HERO header — large cover thumb left, identity middle, actions right */}
      <div className="flex flex-wrap items-start gap-5">
        <div
          className="h-[124px] w-[160px] shrink-0 rounded-xl border border-[var(--app-divider)] bg-[var(--app-bg)]"
          style={{
            backgroundImage: hotel?.coverImageUrl ? `url('${hotel.coverImageUrl}')` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-label={hotel?.name ?? 'Hotel cover'}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="hestia-marketing-rise">
              <h1 className="font-serif text-display font-normal leading-[1.05] tracking-[-0.02em] text-[var(--ink-700)]">
                {hotel?.name ?? '…'}
              </h1>
              <span
                aria-hidden="true"
                className="mt-1.5 block h-[3px] w-12 rounded-full"
                style={{ background: 'var(--accent-highlight-strong)' }}
              />
            </div>
            {hotel && (
              <button
                type="button"
                onClick={() => toggleActive.mutate(!hotel.isActive)}
                disabled={toggleActive.isPending}
                title={hotel.isActive ? 'Inaktiválás' : 'Aktiválás'}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-meta font-semibold transition-opacity hover:opacity-80 ${
                  hotel.isActive
                    ? 'bg-[var(--brand-50)] text-[var(--brand-700)]'
                    : 'bg-[var(--app-divider)] text-[var(--fg-muted)]'
                }`}
              >
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${
                    hotel.isActive ? 'bg-[var(--brand-500)]' : 'bg-[var(--fg-faint)]'
                  }`}
                />
                {hotel.isActive ? 'Aktív' : 'Inaktív'}
              </button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-body text-[var(--fg-2)]">
            {hotel?.contactName && <span>{hotel.contactName}</span>}
            {hotel?.websiteUrl && (
              <>
                <span className="text-[var(--fg-faint)]">·</span>
                {isSafeHttpUrl(hotel.websiteUrl) ? (
                  <a
                    href={hotel.websiteUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="font-mono text-[var(--brand-700)] hover:underline"
                  >
                    {safeUrlHost(hotel.websiteUrl)}
                  </a>
                ) : (
                  <span className="font-mono text-[var(--fg-2)]">
                    {safeUrlHost(hotel.websiteUrl)}
                  </span>
                )}
              </>
            )}
            {hotel && (
              <>
                <span className="text-[var(--fg-faint)]">·</span>
                <span>{hotel.defaultPostFrequencyPerWeek} poszt/hét</span>
              </>
            )}
            {sinceLabel && (
              <>
                <span className="text-[var(--fg-faint)]">·</span>
                <span>{sinceLabel}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => toggleFavorite.mutate()}
            disabled={toggleFavorite.isPending}
            aria-pressed={isFavorite}
            title={isFavorite ? 'Eltávolítás a kedvencek közül' : 'Kedvencekhez adás'}
            className={`inline-flex h-9 items-center gap-1.5 rounded-md border px-4 text-body font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              isFavorite
                ? 'border-[var(--brand-500)] bg-[var(--brand-50)] text-[var(--brand-700)] hover:bg-[var(--brand-100)]'
                : 'border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]'
            }`}
          >
            <StarIcon size={16} filled={isFavorite} />
            {isFavorite ? 'Kedvenc' : 'Kedvencekhez'}
          </button>
          <button
            type="button"
            onClick={() => recrawl.mutate()}
            disabled={recrawl.isPending || !hotel?.websiteUrl}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-4 text-body font-semibold text-[var(--ink-700)] hover:bg-[var(--app-hover)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RotateCcwIcon size={16} /> {recrawl.isPending ? 'Indítás…' : 'Weboldal újra olvasása'}
          </button>
          <Link
            to="/app/plans/new"
            search={{ hotelId }}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-[var(--brand-500)] px-4 text-body font-semibold text-white hover:bg-[var(--brand-600)]"
          >
            📅 Terv létrehozása
          </Link>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            aria-label="Hotel törlése"
            title="Hotel törlése"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--fg-faint)] hover:border-[var(--st-rejected-bg)] hover:bg-[var(--st-rejected-bg)] hover:text-[var(--danger)]"
          >
            <TrashIcon size={16} />
          </button>
        </div>
      </div>

      {confirmDelete && (
        <HotelDeleteConfirm
          hotelName={hotel?.name}
          counts={cascadeCounts}
          confirmText={confirmText}
          onConfirmTextChange={setConfirmText}
          onConfirm={() => remove.mutate()}
          onCancel={() => {
            setConfirmDelete(false);
            setConfirmText('');
          }}
          deleting={remove.isPending}
          deleteError={(remove.error as Error | null) ?? null}
        />
      )}

      {/* The trigger can be rejected (bad URL scheme, a crawl already in
          flight, Redis down). Without this the button just goes back to its
          idle label and nothing happens — the same silent failure the new-hotel
          page had. */}
      {recrawl.error && (
        <p className="text-body text-[var(--danger)]">
          A beolvasás nem indult el: {(recrawl.error as Error).message}
        </p>
      )}

      <IntakeStatusBanner
        hotelId={hotelId}
        onComplete={() => {
          void qc.invalidateQueries({ queryKey: ['hotel-profile', hotelId] });
          void qc.invalidateQueries({ queryKey: ['hotel', hotelId] });
        }}
      />

      {/* Tabs row — inline, underline-on-active per the design spec */}
      <nav className="flex items-end gap-1 border-b border-[var(--app-divider)]">
        {[
          { to: '/app/hotels/$hotelId', label: 'Profil' },
          { to: '/app/hotels/$hotelId/calendar', label: 'Naptár' },
          { to: '/app/hotels/$hotelId/events', label: 'Események' },
          { to: '/app/hotels/$hotelId/media', label: 'Média' },
          { to: '/app/hotels/$hotelId/activity', label: 'Aktivitás' },
          { to: '/app/hotels/$hotelId/archive', label: 'Archív tervek' },
          { to: '/app/hotels/$hotelId/settings', label: 'Beállítások' },
        ].map((t) => (
          <Link
            key={t.to}
            to={t.to}
            params={{ hotelId }}
            activeOptions={{ exact: t.to === '/app/hotels/$hotelId' }}
            className="-mb-px border-b-2 border-transparent px-4 py-2.5 text-body text-[var(--fg-2)] hover:text-[var(--ink-700)]"
            activeProps={{
              className:
                '-mb-px border-b-2 border-[var(--brand-500)] px-4 py-2.5 text-body font-semibold text-[var(--brand-700)]',
            }}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      <div>
        <Outlet />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId')({
  component: HotelDetailShell,
});
